<template>
    <div class="task-header" data-testid="task-header">
        <v-card flat class="header-card" data-testid="task-header__card">
            <v-card-text class="d-flex flex-row justify-space-between align-center pa-0" data-testid="task-header__card-body">
                <div class="w-50" data-testid="task-header__left">
                    <!-- Back button and breadcrumbs navigation -->
                    <div class="d-flex align-center mb-2" data-testid="task-header__nav">
                        <v-btn data-testid="task-header__back-btn" icon class="mr-2" @click="goBack" aria-label="Go back to task list">
                            <v-icon data-testid="task-header__back-icon">mdi-arrow-left</v-icon>
                        </v-btn>
                        <lb-breadcrumbs :items="breadcrumbs" data-testid="task-header__breadcrumbs" />
                    </div>
                    <!-- Task metadata -->
                    <div class="task-header-info d-flex align-center" data-testid="task-header__info">
                        <v-chip data-testid="task-header__ref-chip"  :color="getStatusColor(task.ref)" text-color="primary" class="mr-2 grey lighten-4 rounded-lg justify-center refChip">
                           <span>{{ task.ref || 'Pending' }}</span>
                        </v-chip>
                        <h3 class="task-desc font-weight-medium" data-testid="task-header__description">{{ task.description }}</h3>
                    </div>
                </div>
                <div class="w-50 d-flex align-center justify-end flex-wrap gap-10" data-testid="task-header__actions">
                    <v-chip data-testid="task-header__status-pending-submission" color="" class="pending-sub-chip pa-0 statusChipHeader" v-if="task.stage === 0">
                        <!-- <v-icon>mdi-circle-small</v-icon> -->
                        <span class="status-empty-indicator status-indicator-width-height-10"></span>
                        <span> Pending Submission</span>
                    </v-chip>
                    <v-chip data-testid="task-header__status-processing" color="" class="pending-approval-chip pa-0 statusChipHeader" v-else-if="task.stage === 1">
                        <!-- <v-icon>mdi-circle-small</v-icon> -->
                        <span class="status-empty-indicator status-indicator-width-height-10"></span>
                        <span> Processing</span>
                    </v-chip>
                    <v-chip data-testid="task-header__status-pending-approval" color="" class="pending-approval-chip pa-0 statusChipHeader" v-else-if="task.stage === 2">
                        <!-- <v-icon>mdi-circle-small</v-icon> -->
                        <span class="status-empty-indicator status-indicator-width-height-10"></span>
                        <span> Pending Approval</span>
                    </v-chip>
                    <v-chip data-testid="task-header__status-closed" color="" class="closed-task-chip pa-0 statusChipHeader" v-else-if="task.stage === 3">
                        <!-- <v-icon>mdi-circle-small</v-icon> -->
                        <span class="status-empty-indicator status-indicator-width-height-10"></span>
                        <span> Approved</span>
                    </v-chip>
                    <v-chip data-testid="task-header__status-cancelled" color="" class="cancel-task-chip pa-0 statusChipHeader" v-else-if="task.stage === -1">
                        <!-- <v-icon>mdi-circle-small</v-icon> -->
                        <span class="status-empty-indicator status-indicator-width-height-10"></span>
                        <span> Cancelled</span>
                    </v-chip>
                    <!-- Flag for Matched/Unmatched or Validation Skipped -->
                    <template v-if="allowedTaskTypes">
                      <v-chip
                          data-testid="task-header__validation-skipped-flag"
                          color="warning"
                          class="white--text font-weight-bold rounded-xl"
                          v-if="task.skipValidations"
                      >
                          <span>Validation Skipped</span>
                      </v-chip>
                      <v-chip
                          v-else
                          data-testid="task-header__match-flag"
                          :color="matchFlag ? 'success' : 'error'"
                          class="white--text font-weight-bold rounded-xl"
                      >
                         <span>{{ matchFlag ? 'Matched' : 'Unmatched' }}</span>
                      </v-chip>
                    </template>
                    <!-- refresh button -->
                    <v-btn data-testid="task-header__refresh-btn" @click="refreshData"  class="secondaryBtn" color="" :loading="loading">
                        <v-icon data-testid="task-header__refresh-icon">mdi-refresh</v-icon>
                    </v-btn>
                    <!-- Download Template button for reconciliation -->
                    <!-- <v-btn
                        class="ml-2 primaryBtn"
                        v-if="isReconciliationTask" 
                        @click="downloadReconciliationTemplate()"
                    >
                    <v-icon left>mdi-download</v-icon>
                        Download Template
                    </v-btn> -->
                    <v-btn
                        data-testid="task-header__upload-btn"
                         color="" class="primaryBtn"
                        v-if="isReconciliationTask"
                        @click="handleOpenUploadDrawer"
                    >
                        <v-icon left data-testid="task-header__upload-icon">mdi-upload</v-icon>
                        <span>Upload</span>
                    </v-btn>
                    <v-btn
                        data-testid="task-header__submit-btn"
                         class="primaryBtn"
                        v-if="isReconciliationTask"
                        :loading="submitLoading"
                        @click="handleSubmitTask"
                    >
                        <v-icon left data-testid="task-header__submit-icon">mdi-check-circle</v-icon>
                        <span>Submit</span>
                    </v-btn>
                    
                    <v-tooltip bottom :disabled="task.stage !== 0">
                        <template v-slot:activator="{ on, attrs }">
                            <div v-bind="attrs" v-on="on" style="display: inline-block;">
                                <v-btn 
                                    data-testid="task-header__reject-btn"  
                                    color="cursor-pointer" 
                                    class="secondaryBtn" 
                                    v-if="isApprover && !isClosedTask"  
                                    @click="handleReject" 
                                    :disabled="task.stage===0"
                                >
                                    <span>Reject</span>
                                </v-btn>
                            </div>
                        </template>
                        <span>Task must be submitted before rejection</span>
                    </v-tooltip>

                    <v-menu
                        v-model="approverMenu"
                        :close-on-content-click="false"
                        offset-y
                        nudge-bottom="10"
                        left
                        v-if="isApprover && !isClosedTask"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn 
                                data-testid="task-header__approve-btn" 
                                color="primary" 
                                class="text-capitalize"
                                v-bind="attrs"
                                v-on="on"
                                :disabled="task.stage === 0"
                            >
                                <span>Approve</span>
                            </v-btn>
                        </template>
                        <v-card min-width="300" data-testid="task-header__approve-menu">
                            <v-card-title class="text-subtitle-1 pb-0">Approval Reason</v-card-title>
                            <v-card-text>
                                <lb-textBox
                                    data-testid="task-header__approve-reason-input"
                                    requriedrows="3"
                                    :hidedetails="true"
                                    v-model="approver_remark"
                                    label="Reason (Optional)"
                                />
                            </v-card-text>
                            <v-card-actions class="pt-0">
                                <v-spacer></v-spacer>
                                <v-btn
                                    color="primary"
                                    @click="confirmApprove"
                                    data-testid="task-header__approve-confirm-btn"
                                    class="text-capitalize"
                                >
                                    <span>Approve</span>
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-menu>
                    <!-- User Role Switch Dropdown -->
                    <v-select
                        data-testid="task-header__role-select"
                        v-model="selectedUserType"
                        :items="filteredUserTypeOptions"
                        item-text="label"
                        item-value="value"
                        label="Switch User"
                        outlined
                        dense
                        hide-details
                        class="role-select"
                        @change="handleUserTypeChange"
                    />
                </div>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import { getTbTotals, calculateUnexplainedDifference } from '@/modules/financialclose/utils/utils';

export default {
    name: 'TaskHeader',
    props: {
        task: {
            type: Object,
            required: true
        },
        breadcrumbs: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        },
        addeditdata: { type: Object, required: true },
        addeditdataerror: { type: Object, default: () => ({}) },
        addeditloading: { type: Boolean, default: false },
        threshold: { type: [Number, String], default: 0 },
    },
    data() {
        return {
            sendforreviewdrawer: false,
            taskTypes: this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.ACCOUNT_RECONCILIATION,
            userTypeOptions: this.$constants.TASK_LIST_CONSTANTS.ROLE_OPTIONS,
            selectedUserType: this.$route.query.userType || 'preparer',
            submitLoading: false,
            approverMenu: false,
            approver_remark: ''
        };
    },
    methods: {
        async handleSubmitTask() {
            this.submitLoading = true;
            try {
                const cycleId = this.task.project;
                const taskId = this.task._id;
                if (!cycleId || !taskId) throw new Error('Missing cycle or task ID');
                // Call Vuex action and get response
                const response = await this.$store.dispatch('tasks/submitTask', { taskId, cycleId });
                if (response?.status === 'success') {
                    this.$emit('task-submitted', response);
                    this.refreshData();
                }
            } catch (err) {
                // Error snackbar already handled inside Vuex action
            } finally {
                this.submitLoading = false;
            }
        },
        handleUserTypeChange(val) {
            // Emit to parent to update userType in route/query/store
            this.$emit('user-type-change', val);
        },
        confirmApprove() {
            // Emit the approve event with optional reason
            this.$emit('change-status', { 
                id: this.task._id, 
                status: 'approve',
                approver_remark: this.approver_remark || ''
            });
            // Close menu and reset reason
            this.approverMenu = false;
            this.approver_remark = '';
        },
        handleReject() {
            this.$emit('change-status', { id: this.task._id, status: 'reject' });
        },
        goBack() {
            // Emit event to parent to handle navigation
            this.$emit('back');
        },
        refreshData() {
            // Emit event to parent to handle refresh
            this.$emit('refresh');
        },
        getStatusColor(status) {
            const statusColors = {
                'Completed': 'success',
                'In Progress': 'primary',
                'Pending': 'warning',
                'Overdue': 'error'
            };
            return statusColors[status] || 'grey';
        },
        handleAddEdit() {
            this.$emit('add-edit-checklist-item');
        },
        downloadReconciliationTemplate() {
            // Directly call the template API endpoint, no id needed
            // TODO: Change V2 to V3 after testing
            this.$nova.downloadFile(
                "",
                {},
                "/v2/financialclose/analysis/gettasklisttemplateV3"
            );
        },
        handleOpenUploadDrawer() {
            this.$emit('open-upload-drawer',"task-header");
        },
    },
    computed: {
        taskListData() {
            return this.$store.getters['tasks/getTaskListData'] || {};
        },
        filteredUserTypeOptions() {
            // Only show roles (manager, preparer, approver) for which the current user is assigned in the task
            const user = this.$store?.state?.user;
            const userId = user?.id;
            let allowed = [];
            // Check for each role if the user is assigned
            if (userId) {
                if (Array.isArray(this.task.preparer) && this.task.preparer.includes(userId)) allowed.push('preparer');
                else if (typeof this.task.preparer === 'string' && this.task.preparer === userId) allowed.push('preparer');
                if (Array.isArray(this.task.approver) && this.task.approver.includes(userId)) allowed.push('approver');
                else if (typeof this.task.approver === 'string' && this.task.approver === userId) allowed.push('approver');
                // Manager check: also check taskListData.metadata.managerlist
                let managerList = [];
                if (this.taskListData && this.taskListData.metadata && Array.isArray(this.taskListData.metadata.managerlist)) {
                    managerList = this.taskListData.metadata.managerlist;
                }
                if ((Array.isArray(this.task.manager) && this.task.manager.includes(userId)) ||
                    (typeof this.task.manager === 'string' && this.task.manager === userId) ||
                    (managerList.includes(userId))) {
                    allowed.push('manager');
                }
            }
            // If allowedRoles is present, further filter
            if (Array.isArray(this.task.allowedRoles) && this.task.allowedRoles.length > 0) {
                allowed = allowed.filter(role => this.task.allowedRoles.includes(role));
            }
            // If none, fallback to all
            if (allowed.length === 0) {
                allowed = this.userTypeOptions.map(opt => opt.value);
            }
            // Only show roles that are both allowed and in userTypeOptions
            return this.userTypeOptions.filter(opt => allowed.includes(opt.value));
        },
        allowedTaskTypes() {
            return this.taskTypes === this.task.task_type;
        },
        isClosedTask() {
            // Consider task closed if status is false, statusdetail is contains 'approve' (case-insensitive)
            const t = this.task || {};
            const statusdetail = typeof t.statusdetail === 'string' ? t.statusdetail.toLowerCase() : '';
            return (
                t.status === false ||
                statusdetail.includes('approve')
            );
        },
        viewtaskdata() {
            return this.task || {};
        },
        isReconciliationTask() {
            const userType = this.$route.query.userType;
            return userType === 'preparer';
        },
        isApprover() {
            const userType = this.$route.query.userType;
            return userType === 'approver';
        },
        tbtotals() {
            return getTbTotals(this.viewtaskdata);
        },
        unexplainedDiff() {
            return calculateUnexplainedDifference(this.viewtaskdata);
        },
        matchFlag() {
            // Use computed unexplainedDiff
            const unexplained = Number(this.unexplainedDiff);
            const thresh = Number(this.threshold);
            if (isNaN(thresh) || thresh === null || thresh === undefined) return false;
            return Math.abs(unexplained) <= thresh;
        },
    },
    watch: {
        filteredUserTypeOptions: {
            handler(newFiltered) {
                // Auto-select if only one role is available
                if (newFiltered.length === 1 && this.selectedUserType !== newFiltered[0].value) {
                    this.$nextTick(() => {
                        this.selectedUserType = newFiltered[0].value;
                        this.handleUserTypeChange(newFiltered[0].value);
                    });
                }
            },
            immediate: true
        }
    }
}
</script>

<style scoped>
.header-card {
    border-radius: 4px;
}
.role-select{
    max-width: 140px;
    min-width: 100px;
}
.role-select .v-input__control {
    min-height: 38px;
    background: #f8fafd;
    border-radius: 6px;
    border: 1px solid #e0e4ea;
    transition: border-color 0.2s;
}
.role-select .v-input__slot {
    padding-left: 6px;
}
.role-select .v-select__selections {
    font-weight: 500;
    color: #3a3a3a;
}
.role-select .v-label {
    color: var(--color-primary) !important;
    font-weight: 500;
    letter-spacing: 0.01em;
}
.task-header-info {
    /* Placeholder styles for task header info section */
    min-height: 24px;
}

.w-50{
    width: 50%;
}
.refChip ::v-deep .v-chip__content {
    justify-content: center;
}
.gap-10{
    gap: 10px;
}
.task-desc {
    line-height: normal;
}
.v-input {
    font-size: var(--font-size-xs);
}
</style>
